<template>
	<div class="partial partial--checkout checkout">

		<template v-if="sessionId">
			<h2>Processing...</h2>
			<div class="checkout__pricing">
				<div class="completing">
					<LoadingSpinner></LoadingSpinner>
					<div>
						<h3>Completing your transaction.</h3>
						<h4 class="mt-1">If this is taking longer than expected, please contact support.</h4>
					</div>
				</div>
			</div>
		</template>

		<template v-if="!sessionId">
			<h2>Let's Get Started</h2>
			<h3>Full Access. Unlimited Cases.</h3>
			<div class="checkout__pricing" v-if="product">

				<div class="matrix">
					<div class="head users">
						<h3>Users</h3>
					</div>
				<template v-for="(price, priceIdx) in product?.prices">
					<div class="head" :class="price.interval">
						<h3 v-html="price.description"></h3>
						<small v-html="price.metadata.label"></small>
						<span class="tag" v-if="price.interval == 'year'">Best Value</span>
					</div>
				</template>

				<template v-for="(tier, tierIdx) in product?.prices[0]?.tiers">
					<div class="cell users">
						{{ tierIdx == 0 ? '1' : product?.prices[0]?.tiers[tierIdx - 1].up_to + 1 }}
						{{ tier.up_to ? `- ${tier.up_to}` : '+'}}
					</div>
					<div class="cell" :class="product?.prices[0].interval">
						<h3 v-html="$filters.currency((product?.prices[0].tiers[tierIdx].unit_amount / ( product?.prices[0].interval == 'year' ? 12 : 1) ) / 100, false, 0)"></h3>
						 <span>/mo</span>
					</div>
					<div class="cell" :class="product?.prices[1].interval">
						<h3 v-html="$filters.currency((product?.prices[1].tiers[tierIdx].unit_amount / ( product?.prices[1].interval == 'year' ? 12 : 1) ) / 100, false, 0)"></h3>
						 <span>/mo</span>
					</div>
				</template>
				<div class="cell users select">
					<div class="quantity">
						<IconButton @click="priceQuantity--" :disabled="priceQuantity <= 1" size="small">remove</IconButton>
						<div class="quantity__number">
							<input type="tel" v-model="priceQuantity" class="quantity__value" size="3" /><br />
						</div>
						<IconButton @click="priceQuantity++" size="small">add</IconButton>
					</div>
				</div>

				<div class="cell year select">
					<div>
						<h3>{{ $filters.currency(costTotal.year?.total) }}</h3><span>/mo</span>
					</div>
					<small class="savings">You Save {{ costTotal.year?.savings }}%</small>
					<h4 class="trial">Includes 7-day Free Trial!</h4>
					<Button look="form" post="keyboard_arrow_right" @click="selectedPrice = product?.prices[0].priceId; doCheckout()" :loading="isLoading && selectedPrice == product?.prices[0].priceId" :disabled="isLoading">Start {{ product?.prices[0]?.description }}</Button>

				</div>
				<div class="cell month select">
					<div>
						<h3>{{ $filters.currency(costTotal.month?.total) }}</h3><span>/mo</span>
					</div>
					<small class="savings" v-if="costTotal.month?.savings != 0">
						<!-- <s>{{ $filters.currency(priceTotals.month?.saveCost) }}/mo</s> -->
						You Save {{ costTotal.month?.savings }}%
					</small>
					<h4 class="trial">Includes 7-day Free Trial!</h4>
					<Button look="form" post="keyboard_arrow_right" @click="selectedPrice = product?.prices[1].priceId; doCheckout()" :loading="isLoading && selectedPrice == product?.prices[1].priceId" :disabled="isLoading">Start {{ product?.prices[1]?.description }}</Button>
				</div>
				</div>
			</div>
			<div class="checkout__pricing" v-else>
				<div class="completing">
					<LoadingSpinner></LoadingSpinner>
					<div>
						<h3>Loading up some killer deals for you...</h3>
						<h4 class="mt-1">If this is taking longer than expected, please contact support.</h4>
					</div>
				</div>
			</div>
		</template>

		<Modal :open="showModal" @close="showModal = false" title="Your Account">
			<template v-slot:content>
				<component :is="authComponent" v-bind="{routeLink: false}" :checkout="true" @close="showModal = false" />
			</template>
		</Modal>
	</div>

</template>

<script>
import { useHead } from '@unhead/vue'
import { useMeStore } from '@/lib/store/me'
import { useUserStore } from '@/lib/store/user'

import product from '@/assets/product.json'

import Button from '@/components/Button.vue'
import Card from '@/components/Card.vue'
import IconButton from '@/components/IconButton.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import LoginForm from '@/components/LoginForm.vue'
import Modal from '@/components/Modal.vue'
import RegisterForm from '@/components/RegisterForm.vue'
import TextField from '@/components/TextField.vue'

import AuthLogin from './AuthLogin.vue'
import AuthRegister from './AuthRegister.vue'
import AuthSelect from './AuthSelect.vue'
import AuthVerify from './AuthVerify.vue'

export default {
	name: 'PartialCheckout',
	components: {
		Button,
		Card,
		IconButton,
		LoadingSpinner,
		LoginForm,
		Modal,
		RegisterForm,
		TextField,

		AuthLogin,
		AuthRegister,
		AuthSelect,
		AuthVerify,
	},
	setup() {
		const meStore = useMeStore()
		const userStore = useUserStore()

		useHead({
			titleTemplate: `Pricing | Sentencing Pro`,
			title: `Check Out`,
		})

		return {
			meStore,
			userStore,
		}
	},
	data: () => ({
		activeTab: 0,
		products: [],
		product: product,
		selectedPrice: null,
		priceQuantity: 1,
		showRegister: true,
		showModal: false,
		priceTotals: {},
	}),
	async mounted() {
		try {
			if (this.$route.query?.session_id) {
				this.userStore.checkoutComplete(this.$route.query.session_id)
					.then(res => {
						this.$router.replace({name: 'account'})
					})
			}
		} catch (error) {

		}

		// this.product = await this.userStore.getProduct(import.meta.env.VITE_PRODUCT_ID)
		this.selectedPrice = this.product?.prices?.[0]?.priceId
	},
	computed: {
		sessionId() {
			return this.$route.query?.session_id
		},
		me() {
			return this.userStore.me
		},
		steps() {
			if (this.me?.uid) {
				return [
					'Plan',
					// 'Payment',
				]
			} else {
				return [
					'Plan',
					'Account',
					// 'Payment',
				]
			}
		},
		authData() {
			return this.userStore.authData
		},
		authClaims() {
			return this.userStore.claims
		},
		authStep() {
console.log('[authStep]', this.userStore.authStep)
			if (this.authClaims?.metadata?.userLevel == 'demo' ) return 'register'
			if (!this.authClaims?.uid) return this.userStore.authStep == 'login' ? 'login' : 'register'
			return this.userStore.authStep
		},
		checkoutAgreement() {
			return this.userStore.checkoutAgreement
		},
		authComponent() {
			console.log('[authComponent]', this.authStep, this.authClaims)
			switch(this.authStep) {
				case 'login':
					return AuthLogin
				case 'verify':
					return AuthVerify
				case 'select':
					return AuthSelect
				case 'register':
					return AuthRegister
			}
		},
		isLoading() {
			return this.userStore.isLoading
		},
		costTotal() {
			let priceTotals = {}

			if (!this.product?.prices) 	return {total: 0, savings: 0}

			for (const price of this.product?.prices) {
				const div = price.interval == 'year' ? 12 : 1
				const tierIdx = price?.tiers.findIndex(t => this.priceQuantity <= (t.up_to ? t.up_to : 10000))
				const tier = price?.tiers[tierIdx]

				if (tier) {
					let savings = 0
					let saveCost = 0
					let total = (this.priceQuantity * tier.unit_amount) / div / 100

					// if (tierIdx > 0) {
						const saveTier = this.product?.prices[1]?.tiers[0]
						saveCost = (this.priceQuantity * saveTier.unit_amount) / 100
						// const saveTier = price?.tiers[tierIdx - 1]
						savings = Math.round((1 - (total / saveCost)) * 100)
						// savings = saveCost
					// }
					priceTotals[price.interval] = {
						total,
						savings,
						saveCost,
					}
				}
			}

			return priceTotals
			return {total: 0, savings: 0}
		},
	},
	methods: {
		async doCheckout() {
			console.log('[doCheckout]', this.authClaims)
			if (!this.authClaims?.uid || this.authClaims?.metadata?.userLevel == 'demo') return this.showModal = true
			if (!this.selectedPrice || !this.priceQuantity || !this.authClaims?.uid) return
			const checkoutParams = {
				price: this.selectedPrice,
				quantity: this.priceQuantity,
			}

			const code = this.$route.query.code
			if (code) {
				checkoutParams.code = code
			}

			this.userStore.checkout(this.authClaims.uid, checkoutParams)
				.then(res => {
					console.log('RES', res)
					window.location.assign(res.url)
				})
		},
		tierCheck(price, tierIdx) {
			const tier = price?.tiers[tierIdx]
			if (tierIdx <= 0) {
				const up_to = tier.up_to ?? 1000
				if (this.priceQuantity <= up_to) {
					return true
				}
			} else {
				const prevTier = price.tiers[tierIdx - 1]
				const up_to = tier.up_to ?? 1000
				if (this.priceQuantity > prevTier.up_to && this.priceQuantity <= up_to) {
					return true
				}
			}

			return false
		},
	},
	watch: {
		me(newVal, oldVal) {
console.log('[me.watch]', { me: newVal, oldVal})
			if (!newVal) return
			if (!this.selectedPrice) return
			if (this.priceQuantity <= 0) return

			return this.doCheckout()
		},
		activeTab(newVal, oldVal) {
console.log('[activeTab.watch]', { me: newVal, oldVal})

			if (oldVal == newVal) return
			if (!this.selectedPrice) return
			if (this.priceQuantity <= 0) return

			if (newVal == 1 && this.me?.uid) {
				return this.doCheckout()
			}
		},
		authClaims(newVal, oldVal) {
console.log('[watch.authData]', newVal, this.selectedPrice, this.priceQuantity, oldVal)
			if (JSON.stringify(oldVal) == JSON.stringify(newVal)) return

			if (newVal.uid) {
				this.showModal = false
				if (!this.selectedPrice) return
				if (this.priceQuantity <= 0) return

				return this.doCheckout()
			}
		},
		authStep(newVal, oldVal) {
			console.log('[authStep]', { newVal, oldVal, })
			if (!newVal && oldVal) {
				this.showModal = false
			}
			switch(newVal) {
				case 'complete':
					this.$nextTick().then(() => {
						const checkoutParams = {
							price: this.selectedPrice,
							quantity: this.priceQuantity,
						}

						this.userStore.checkout(this.authData.uid, checkoutParams)
							.then(res => {
								console.log('RES', res)
								window.location.assign(res.url)
							})

						this.showModal = false
					})
					break
			}

		},
	},
}
</script>

<style scoped lang="scss">
.checkout {
	display: flex;
	flex-direction: column;
	align-items: center;
	// justify-content: center;

	h2 {
		font-family: var(--font-family--serif);
	}

	&__body {
		background-color: var(--color--background);
		border: 1px solid var(--color--border-light);
		border-top-width: 0;
		max-width: 540px;
		width: 100%;
		margin-left: auto;
		margin-right: auto;
		border-bottom-left-radius: var(--border-radius);
		border-bottom-right-radius: var(--border-radius);
	}

	&__heading {
		max-width: 540px;
		width: 100%;
		margin-left: auto;
		margin-right: auto;
	}

	&__section {
		&-body {
			padding: 1rem;
		}
	}

	&__actions {
		background-color: rgba(255,255,255,0.8);
		border-radius: 1rem;
		box-shadow: inset 0 1px 1px 0 hsla(0,0%,100%,.1),0 50px 100px -20px rgba(50,50,93,.25),0 30px 60px -30px rgba(0,0,0,.3);
		margin: 0;
		display: grid;
		grid-template-columns: 0.5fr 1fr 1fr 1fr;
		padding: 1rem;
		align-items: center;
	}

	&__pricing {
		max-width: 960px;
		width: 100%;
	}

}

.quantity {
	display: flex;
	flex-direction: row;
	margin-left: auto;
	margin-right: auto;
	width: fit-content;

	&__number {
		text-align: center;

		small {
			color: var(--color--primary-text--light);
		}
	}

	&__value {
		font-size: 1.2rem;
		font-weight: 450;
		line-height: 2.375rem;
		text-align: center;
	}

}

.completing {
	background-color: rgba(255,255,255,0.8);
	border-radius: 1rem;
	box-shadow: inset 0 1px 1px 0 hsla(0,0%,100%,.1),0 50px 100px -20px rgba(50,50,93,.25),0 30px 60px -30px rgba(0,0,0,.3);
	margin: 3rem 0;
	display: grid;
	grid-template-columns: auto 1fr;
	gap: 1rem;
	padding: 1rem;
	overflow: hidden;
}

.matrix {
	border-radius: 1rem;
	box-shadow: inset 0 1px 1px 0 hsla(0,0%,100%,.1),0 50px 100px -20px rgba(50,50,93,.25),0 30px 60px -30px rgba(0,0,0,.3);
	display: grid;
	gap: 1px;
	grid-template-columns: 0.75fr 1fr 1fr;
	margin: 3rem 0;
	overflow: hidden;

	.tag {
		font-size: 0.8rem;
		padding: 0.2rem 0.4rem;
		border-radius: 0.4rem;
		background-color: rgba(89, 213, 124, 0.8);
		position: absolute;
		right: 0.75rem;
		top: 0.75rem;

		@include modules.media-query('phone') {
			position: relative;
			display: block;
			right: auto;
		}
	}

	.head,
	.cell {
		background-color: rgba(255,255,255,0.8);
		display: block;
		place-content: center;
		padding: 0.75rem;
		position: relative;
		text-align: center;

		@include modules.media-query('phone') {
			padding: 0.75rem 0.25rem;
		}
	}

	.head {
		&.users {
			background-color: rgba(255,255,255,0.6);
		}

		&.year {
			background-color: rgba(43, 167, 255, 0.222);

			h3 {
				font-size: 1.3rem;
				font-weight: 500;
				margin-top: -3px;
			}
		}

		h3 {
			font-weight: 400;
			margin-bottom: 0;
		}

		small {
			color: var(--color--text--light);
		}
	}

	.cell {
		&--span-2 {
			grid-column: span 2;
		}

		h3 {
			display: inline-block;
			font-size: 1.5rem;
			font-weight: 400;
			margin: 0;
		}

		span {
			color: var(--color--text--light);
		}

		&.year {
			&:not(.cell--span-2) {
				background-color: rgba(250, 252, 254, 0.522);
				// border-left: 1px solid rgba(43, 167, 255, 0.222);
				// border-right: 1px solid rgba(43, 167, 255, 0.222);
			}
		}

		&.select {
			border-top: 1px solid var(--color--primary);
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			gap: 0.5rem;
		}
	}

	.savings {
		color: rgb(14, 150, 52);
		float: right;
		position: absolute;
		right: 1rem;

		@include modules.media-query('phone') {
			position: relative;
		}
	}

	.trial {
		background-color: rgba(89, 213, 124, 0.2);
		// background-color: rgba(var(--root--color--primary-light), 0.05);
		border: 1px solid rgba(var(--root--color--primary-light), 0.35);
		border-color: rgba(89, 213, 124, 0.8);

		border-bottom: 0;
		color: var(--color--primary);
		border-radius: var(--border-radius-small);
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		padding: 0.5rem 0.5rem 0.5rem 0.5rem;
		transform: translateY(0.35rem);
		margin-bottom: -0.25rem;
	}
}
</style>
